import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, RefreshCw, Users, CreditCard, Download, AlertCircle, ArrowUp, ArrowDown } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_BASE_URL = 'https://apisaleeasy.psisat.com';

const MONTH_OPTIONS = [3, 6, 9, 12];

// 'normal' is the default for most customers, so it carries no signal and gets no badge
const CUSTOMER_TYPE_LABELS = {
  ma: 'ตัวแทนรายใหญ่',
  new: 'ลูกค้าใหม่'
};

const CUSTOMER_TYPE_STYLES = {
  ma: 'bg-purple-100 text-purple-700',
  new: 'bg-blue-100 text-blue-700'
};

const InactiveCreditCustomersComponent = ({ initialMonths = 9 }) => {
  const [months, setMonths] = useState(initialMonths);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sortKey, setSortKey] = useState('last_invoice_date');
  const [sortAsc, setSortAsc] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/credit-limit-mismatch/inactive-credit-customers?months=${months}`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const json = await response.json();
      if (!json.success) throw new Error(json.message || 'API error');

      setRows(json.data.data || []);
      setSummary(json.data.summary || null);
    } catch (err) {
      setError(err.message);
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatNumber = (value) =>
    (value ?? 0).toLocaleString('th-TH', { maximumFractionDigits: 0 });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('th-TH', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const fullName = (row) => `${row.technician_name} ${row.technician_surname}`.trim();

  // Search and sort run on the client — the list is a few hundred rows at most
  const visibleRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    const filtered = query
      ? rows.filter(row =>
          [
            row.technician_code,
            row.technician_name,
            row.technician_surname,
            row.technician_shopname,
            row.technician_phone,
            row.technician_province,
            row.salesman_name,
            row.salesman_code
          ].some(field => (field || '').toLowerCase().includes(query))
        )
      : [...rows];

    const direction = sortAsc ? 1 : -1;
    const comparators = {
      // customers that never billed sort first when ascending — they are the most dormant
      last_invoice_date: (a, b) => {
        if (a.last_invoice_date === b.last_invoice_date) return 0;
        if (!a.last_invoice_date) return -1;
        if (!b.last_invoice_date) return 1;
        return a.last_invoice_date < b.last_invoice_date ? -1 : 1;
      },
      credit_limit: (a, b) => a.credit_limit - b.credit_limit,
      name: (a, b) => fullName(a).localeCompare(fullName(b), 'th'),
      province: (a, b) => (a.technician_province || '').localeCompare(b.technician_province || '', 'th'),
      salesman_name: (a, b) => (a.salesman_name || '').localeCompare(b.salesman_name || '', 'th')
    };
    const comparator = comparators[sortKey] || comparators.last_invoice_date;
    return filtered.sort((a, b) => comparator(a, b) * direction);
  }, [rows, searchText, sortKey, sortAsc]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key === 'last_invoice_date');
    }
  };

  // Tailwind only emits classes it can see as literals, so the alignment is spelled out
  const SortHeader = ({ columnKey, children, align = 'left' }) => (
    <th
      onClick={() => toggleSort(columnKey)}
      className={`px-4 py-2.5 ${align === 'right' ? 'text-right' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 whitespace-nowrap`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {children}
        {sortKey === columnKey && (
          sortAsc ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
        )}
      </span>
    </th>
  );

  const exportExcel = () => {
    if (visibleRows.length === 0) return;
    const data = visibleRows.map((row, index) => ({
      'ลำดับ': index + 1,
      'รหัสลูกค้า': row.technician_code,
      'ชื่อ-สกุล': fullName(row),
      'ชื่อร้าน': row.technician_shopname || '-',
      'จังหวัด': row.technician_province || '-',
      'เบอร์โทร': row.technician_phone || '-',
      'ประเภทลูกค้า': CUSTOMER_TYPE_LABELS[row.technician_major] || 'ลูกค้าทั่วไป',
      'วงเงินเครดิต': row.credit_limit,
      'เปิดบิลล่าสุด': row.last_invoice_date ? formatDate(row.last_invoice_date) : 'ไม่เคยเปิดบิล',
      'ไม่เคลื่อนไหว (วัน)': row.days_since_last_invoice ?? '-',
      'จำนวนบิลทั้งหมด': row.invoice_count,
      'ยอดซื้อสะสม': row.total_amount,
      'เซลล์ผู้ดูแล': row.salesman_name || '-',
      'สถานะ': row.is_disabled ? 'ปิดการใช้งาน' : 'ใช้งาน'
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `ไม่เปิดบิล ${months} เดือน`);
    XLSX.writeFile(workbook, `inactive_credit_customers_${months}m.xlsx`);
  };

  const summaryTiles = summary ? [
    { label: 'ลูกค้าที่มีเครดิตทั้งหมด', value: formatNumber(summary.total_credit_customers), tone: 'text-gray-900' },
    { label: `ไม่เปิดบิล ${summary.inactive_months_window} เดือน`, value: formatNumber(summary.total_inactive), tone: 'text-orange-600' },
    { label: 'ไม่เคยเปิดบิลตั้งแต่เริ่มระบบ', value: formatNumber(summary.never_billed), tone: 'text-red-600' },
    { label: 'เคยเปิดบิลแล้วหยุดไป', value: formatNumber(summary.billed_but_dormant), tone: 'text-amber-600' },
    { label: 'วงเงินเครดิตที่ไม่ถูกใช้', value: formatNumber(summary.total_credit_limit), tone: 'text-blue-600' }
  ] : [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">ลูกค้าที่ยังไม่ใช้เครดิต</h2>
          <p className="text-gray-500 text-sm mt-1">
            ลูกค้าที่มีวงเงินเครดิต แต่ไม่มีการเปิดบิลในช่วง {months} เดือนที่ผ่านมา
          </p>
          {summary && (
            <p className="text-xs text-gray-400 mt-0.5">
              นับบิลตั้งแต่ {formatDate(summary.cutoff_date)} ถึงปัจจุบัน (ไม่รวมบิลที่ถูกยกเลิก) ·
              เริ่มนับจากวันที่เริ่มใช้ระบบ {formatDate(summary.system_start_date)}
            </p>
          )}
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'กำลังโหลด...' : 'Refresh'}
        </button>
      </div>

      {/* Window selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-600 mr-1">ช่วงที่ไม่เปิดบิล:</span>
        {MONTH_OPTIONS.map(option => (
          <button
            key={option}
            onClick={() => setMonths(option)}
            disabled={loading}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 ${
              months === option
                ? 'bg-purple-600 border-purple-600 text-white'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {option} เดือน
          </button>
        ))}
      </div>

      {/* Summary */}
      {summary && !loading && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {summaryTiles.map(tile => (
            <div key={tile.label} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <p className="text-xs text-gray-500 mb-1">{tile.label}</p>
              <p className={`text-xl font-bold ${tile.tone}`}>{tile.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="ค้นหารหัสลูกค้า ชื่อ ร้าน เบอร์โทร จังหวัด หรือเซลล์..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          disabled={loading}
          className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          เกิดข้อผิดพลาด: {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-500">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" />
          <span>กำลังโหลดข้อมูล...</span>
        </div>
      )}

      {/* No results */}
      {!loading && !error && visibleRows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <Users className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-base">
            {searchText.trim() ? `ไม่พบลูกค้าที่ตรงกับ "${searchText}"` : 'ไม่มีลูกค้าในเงื่อนไขนี้'}
          </p>
        </div>
      )}

      {/* Table */}
      {!loading && visibleRows.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-gray-500">
              แสดง {formatNumber(visibleRows.length)} รายการ
              {searchText.trim() && summary && ` จากทั้งหมด ${formatNumber(summary.total_inactive)} รายการ`}
            </p>
            <button
              onClick={exportExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-10">#</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">รหัส</th>
                  <SortHeader columnKey="name">ชื่อลูกค้า</SortHeader>
                  <SortHeader columnKey="province">จังหวัด</SortHeader>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">เบอร์โทร</th>
                  <SortHeader columnKey="credit_limit" align="right">วงเงิน</SortHeader>
                  <SortHeader columnKey="last_invoice_date">เปิดบิลล่าสุด</SortHeader>
                  <SortHeader columnKey="salesman_name">เซลล์ผู้ดูแล</SortHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visibleRows.map((row, index) => (
                  <tr key={row.technician_code} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-gray-400 text-xs">{index + 1}</td>
                    <td className="px-4 py-2.5">
                      <span className="font-mono text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                        {row.technician_code}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{fullName(row) || '-'}</span>
                        {CUSTOMER_TYPE_LABELS[row.technician_major] && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${CUSTOMER_TYPE_STYLES[row.technician_major]}`}>
                            {CUSTOMER_TYPE_LABELS[row.technician_major]}
                          </span>
                        )}
                        {row.is_disabled && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">ปิดการใช้งาน</span>
                        )}
                      </div>
                      {row.technician_shopname && (
                        <p className="text-xs text-gray-500 mt-0.5">{row.technician_shopname}</p>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs whitespace-nowrap">{row.technician_province || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs whitespace-nowrap">{row.technician_phone || '-'}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 font-medium text-gray-900">
                        <CreditCard className="w-3 h-3 text-gray-400" />
                        {formatNumber(row.credit_limit)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {row.never_billed ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 font-medium">
                          ไม่เคยเปิดบิล
                        </span>
                      ) : (
                        <div>
                          <p className="text-xs text-gray-700">{formatDate(row.last_invoice_date)}</p>
                          <p className="text-xs text-gray-400">
                            {formatNumber(row.days_since_last_invoice)} วันที่ผ่านมา · {row.invoice_count} บิล
                          </p>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs whitespace-nowrap">
                      {row.salesman_name ? (
                        <div>
                          <p className="text-gray-700">{row.salesman_name}</p>
                          <p className="text-gray-400 font-mono">{row.salesman_code}</p>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-400">
            เซลล์ผู้ดูแลอ้างอิงจากบิลล่าสุดของลูกค้า ลูกค้าที่ไม่เคยเปิดบิลจึงไม่มีข้อมูลเซลล์
          </p>
        </div>
      )}
    </div>
  );
};

export default InactiveCreditCustomersComponent;
